import { forwardRef } from 'react';
import { customerToSections } from './customerDisplay';

// A4-width (794px @ 96dpi) printable data sheet for a Sohoz Job customer.
// Uses inline styles only so it renders identically when:
//   • shown in the preview modal,
//   • cloned into a print iframe (no external CSS needed),
//   • rasterized by html2canvas for the PDF export.

const FONT = "'Noto Sans Bengali', 'Hind Siliguri', 'Hind', system-ui, sans-serif";

const Section = ({ title, children }) => (
    <div style={{ marginTop: 18 }}>
        <div data-pdf-block style={{
            fontSize: 12, fontWeight: 700, color: '#E68A00', textTransform: 'uppercase',
            letterSpacing: '0.05em', borderBottom: '2px solid #f1d9b3', paddingBottom: 5, marginBottom: 10,
        }}>
            {title}
        </div>
        {children}
    </div>
);

// `block` marks each row as a page-break candidate. Top-level section rows are
// breakable; rows inside an EntryCard are NOT (the whole card is the break unit,
// so a break never lands in the middle of one).
const Rows = ({ rows, block = true }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24, rowGap: 8 }}>
        {rows.map((r, i) => (
            <div key={i} {...(block ? { 'data-pdf-block': '' } : {})} style={{ display: 'flex', gap: 6, fontSize: 12.5, lineHeight: 1.5, minWidth: 0 }}>
                <span style={{ color: '#6b7280', flexShrink: 0 }}>{r.label}:</span>
                <span style={{ color: '#111827', fontWeight: 600, wordBreak: 'break-word' }}>{r.value}</span>
            </div>
        ))}
    </div>
);

const EntryCard = ({ items, index, label }) => (
    <div data-pdf-block style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 10, breakInside: 'avoid' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 8 }}>{label} {index + 1}</div>
        <Rows rows={items} block={false} />
    </div>
);

function CustomerDataDocument({ data, t }, ref) {
    const { header, sections, academic, experience } = customerToSections(data, t);

    return (
        <div
            ref={ref}
            data-pdf-root
            style={{
                width: 794, background: '#fff', color: '#111827', fontFamily: FONT,
                padding: 36, boxSizing: 'border-box',
            }}
        >
            {/* Header */}
            <div data-pdf-block style={{ display: 'flex', alignItems: 'center', gap: 18, borderBottom: '2px solid #111827', paddingBottom: 16 }}>
                <div style={{
                    width: 84, height: 100, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                    border: '1px solid #e5e7eb', background: '#f3f4f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {header.photo
                        ? <img src={header.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 11, color: '#9ca3af' }}>{t('sohozJob.f.photo')}</span>}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    {header.nameBn && <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>{header.nameBn}</div>}
                    {header.nameEn && <div style={{ fontSize: 14, color: '#374151', marginTop: 2 }}>{header.nameEn}</div>}
                    {header.mobile && (
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                            {t('sohozJob.phone')}: <span style={{ fontWeight: 600, color: '#111827' }}>{header.mobile}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Scalar sections */}
            {sections.map((s, i) => (
                <Section key={i} title={s.title}>
                    <Rows rows={s.rows} />
                </Section>
            ))}

            {/* Academic */}
            {academic.length > 0 && (
                <Section title={t('sohozJob.sections.academic')}>
                    {academic.map((items, i) => <EntryCard key={i} items={items} index={i} label={t('sohozJob.academicEntry')} />)}
                </Section>
            )}

            {/* Experience */}
            {experience.length > 0 && (
                <Section title={t('sohozJob.sections.experience')}>
                    {experience.map((items, i) => <EntryCard key={i} items={items} index={i} label={t('sohozJob.experienceEntry')} />)}
                </Section>
            )}

            {/* Signature */}
            {header.signature && (
                <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ textAlign: 'center' }}>
                        <img src={header.signature} alt="" style={{ height: 48, objectFit: 'contain', display: 'block', margin: '0 auto 4px' }} />
                        <div style={{ borderTop: '1px solid #9ca3af', paddingTop: 4, fontSize: 11, color: '#6b7280', minWidth: 160 }}>
                            {t('sohozJob.f.signature')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default forwardRef(CustomerDataDocument);
